import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';

import { Button } from '@/components/ui/button';

// AppErrorBoundary wraps the whole React tree. When a render error
// escapes the deepest catch, the boundary captures it (Sentry SDK
// auto-reports via beforeCapture), then renders a minimal recovery UI
// with a reload button.
//
// Hardcoded English copy — i18n might be the thing that crashed, and
// the boundary needs to render reliably even when downstream context
// (translations, theme) is unavailable. Wrapping the JSON message in
// data-sentry-unmask isn't necessary here: replay won't reach this
// surface because the SDK pauses replay on uncaught exceptions.
/* eslint-disable react/jsx-no-literals -- the recovery UI is intentionally
   hardcoded English: i18n is one of the subsystems that may have crashed, so
   the boundary must render without depending on translations (see above). */

interface Props {
  children: ReactNode;
}

export function AppErrorBoundary({ children }: Props) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div
          role="alert"
          className="flex min-h-screen items-center justify-center bg-background p-6"
        >
          <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center">
            <h1 className="font-heading text-xl font-bold">
              Something went wrong
            </h1>
            <p className="text-sm text-foreground/70">
              We&apos;ve been notified and are looking into it. Try reloading the
              page — if the problem persists, sign out and back in.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => resetError()}>
                Try again
              </Button>
              <Button onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </div>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
